import { SchemaDefinition } from "./schema-definition-interface";
import { SchemaPropertyInfo } from "./schema-property-info";

/**
 * This class is used to validate our stored-... models against our defined schema models. Modules should pass
 * in a collection of model -> schema pairings to the ValidateModels method via implementing IModuleValidationService
 * in order for the module's models to be validated. This class will compare the properties of the model(s) against
 * the corresponding schema and throw an error if a property mismatch is detected, with an window alert being shown
 * at runtime containing information on which property has a mismatch. This class is also used during unit test
 * execution to perform schema validation which may have been missed during runtime dev testing. 
 */
export class SchemaValidator {
	/**
	 * @constructor
	 * Private constructor to prevent this class from being instantiated because typescript doesn't support static classes.
	 */
	private constructor() { }

	/**
	 * Validates the models against the schema definitions
	 * @param modelSchemaCollection The collection of model instance -> schema definition pairings to be validated
	 */
	public static ValidateModels(modelSchemaCollection: [any, SchemaDefinition][]) {
		for (const [modelInstance, schemaDefinition] of modelSchemaCollection) {
			
			if (!SchemaValidator.validateModel(modelInstance, schemaDefinition)) {
				throw new Error(`Model is invalid according to schema defintion. Please verify model and schema are updated and create an IndexedDb DatabaseUpgrade to apply changes.`);
			}
		};
	}

	/**
	 * Validates the schema of the model.
	 * @param model The model to validate.
	 * @param schema The schema to validate.
	 * @returns True if the object is valid, false otherwise.
	 */
	private static validateModel(model: any, schema: SchemaDefinition): boolean {
		// Check for missing properties defined in the schema
		for (const key in schema.Properties) {
			if (!model.hasOwnProperty(key)) {
				window.alert(`DEVELOPER ALERT: Model is missing property defined in ${schema.Name} schema: ${key}`);
				return false;
			}
		}

		for (const key in model) {
			// Check for additional properties not defined in the schema
			if (!schema.Properties.hasOwnProperty(key)) {
				window.alert(`DEVELOPER ALERT: Model has additional property not defined in ${schema.Name} schema: ${key}`);
				return false;
			}
			// Check for model property type different than what is defined in the schema
			if (!this.validatePropertyType(model[key], schema.Properties[key])) {
				window.alert(`DEVELOPER ALERT: Model property type ${typeof model[key]} does not match schema property type ${schema.Properties[key].Type} for property: ${key}`);
				return false;
			}
		}

		return true;
	}

	/**
	 * Validates the two properties are of the same type.
	 * @param modelProperty The model property to validate.
	 * @param schemaPropertyInfo The schema property info to validate.
	 * @returns True if the two properties are of the same type, otherwise false.
	 */
	private static validatePropertyType(modelProperty: any, schemaPropertyInfo: SchemaPropertyInfo): boolean {
		if (schemaPropertyInfo.Type == 'Date') {
			return modelProperty instanceof Date;
		}
		if (typeof modelProperty !== schemaPropertyInfo.Type) {
			return false;
		}
		return true;
	}
}